import { useEffect, useRef, useState } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';

export interface ChartSeries {
  readonly label: string;
  /** Valores ya convertidos a la unidad de presentación. */
  readonly values: readonly number[];
  readonly color: string;
}

interface Props {
  readonly title: string;
  readonly xLabel: string;
  readonly yLabel: string;
  readonly time: readonly number[];
  readonly series: readonly ChartSeries[];
  /** Descripción textual con los valores clave, para quien no ve la gráfica. */
  readonly summary: string;
}

const MAX_POINTS = 2500;

function canvasAvailable(): boolean {
  try {
    return document.createElement('canvas').getContext('2d') !== null;
  } catch {
    return false;
  }
}

function axis(label: string, ink: string, grid: string): uPlot.Axis {
  return { label, stroke: ink, grid: { stroke: grid, width: 1 }, ticks: { stroke: grid, width: 1 } };
}

function thin(time: readonly number[], series: readonly ChartSeries[]): { x: number[]; ys: Array<Array<number | null>> } {
  const stride = Math.max(1, Math.ceil(time.length / MAX_POINTS));
  const idx: number[] = [];
  for (let i = 0; i < time.length; i += stride) idx.push(i);
  if (idx[idx.length - 1] !== time.length - 1) idx.push(time.length - 1);
  return { x: idx.map((i) => time[i]!), ys: series.map((s) => idx.map((i) => (Number.isFinite(s.values[i]!) ? s.values[i]! : null))) };
}

export function Chart({ title, xLabel, yLabel, time, series, summary }: Props): JSX.Element {
  const host = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = host.current;
    if (el === null || time.length < 2) return;
    let plot: uPlot | undefined;
    let observer: ResizeObserver | undefined;
    // uPlot falla de forma asíncrona sin canvas: se comprueba antes y se cae a la tabla de datos.
    if (!canvasAvailable()) {
      setFailed(true);
      return;
    }
    try {
      const { x, ys } = thin(time, series);
      const width = Math.max(el.clientWidth, 280);
      // uPlot dibuja en gris oscuro por defecto: se toma el color del tema para que se lea en modo oscuro.
      const ink = getComputedStyle(el).color;
      const grid = 'rgba(128, 128, 128, 0.28)';
      plot = new uPlot(
        {
          width,
          height: 240,
          legend: { show: true },
          scales: { x: { time: false } },
          axes: [axis(xLabel, ink, grid), axis(yLabel, ink, grid)],
          series: [{ label: 'Tiempo' }, ...series.map((s) => ({ label: s.label, stroke: s.color, width: 2 }))],
        },
        [x, ...ys] as uPlot.AlignedData,
        el,
      );
      if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(() => plot?.setSize({ width: Math.max(el.clientWidth, 280), height: 240 }));
        observer.observe(el);
      }
      setFailed(false);
    } catch {
      // Entornos sin canvas: se conserva la descripción y la tabla de datos.
      setFailed(true);
    }
    return () => {
      observer?.disconnect();
      plot?.destroy();
    };
  }, [time, series, xLabel, yLabel]);

  const rows = (() => {
    const n = Math.min(12, time.length);
    return Array.from({ length: n }, (_, k) => Math.round((k * (time.length - 1)) / Math.max(n - 1, 1)));
  })();

  return (
    <figure className="chart">
      <figcaption>
        <strong>{title}</strong>. {summary}
      </figcaption>
      <div ref={host} className="chart__plot" role="img" aria-label={`${title}. ${summary}`} />
      {failed && <p className="muted small">No se pudo dibujar la gráfica en este entorno; los datos están en la tabla.</p>}
      <details>
        <summary>Ver datos de «{title}»</summary>
        <table>
          <caption className="sr-only">{title}: muestra de valores</caption>
          <thead>
            <tr>
              <th scope="col">{xLabel}</th>
              {series.map((s) => (
                <th scope="col" key={s.label}>
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((i) => (
              <tr key={i}>
                <td>{time[i]!.toFixed(2)}</td>
                {series.map((s) => (
                  <td key={s.label}>{Number.isFinite(s.values[i]!) ? Number(s.values[i]!.toPrecision(4)) : '—'}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}
